import { FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useId, useMemo } from 'react'
import toast from 'react-hot-toast'
import * as endPoints from '@/endpoints'
import useAuth from '@/hooks/useAuth'
import axios from '@/lib/axios'
import { hasRole } from '@/utils/hasRole'
import { withSecondaryIdentifier } from '@/utils/orgDisplayLabel'

const SITE_ADMIN_ROLES = ['Site Admin Role']

export default function ActiveOrgSwitcher() {
    const { activeOrg, isSwitchingActiveOrg, switchActiveOrg, user } = useAuth()
    const selectId = useId()
    const labelId = `${selectId}-label`
    const isSiteAdmin = Boolean(user && hasRole(user, SITE_ADMIN_ROLES))
    const isPartnerStaff = Boolean(user?.isPartnerStaff)
    // Site admins switch across all orgs; partner admins across their own partner's orgs.
    const canSwitch = isSiteAdmin || isPartnerStaff
    const orgsUrl = isSiteAdmin ? endPoints.ORG : endPoints.PARTNER_ORGS

    const { data, isLoading } = useQuery({
        queryKey: ['orgSwitcher', 'orgs', isSiteAdmin ? 'all' : 'partner'],
        queryFn: () => axios.get(orgsUrl).then((response) => response.data),
        enabled: canSwitch,
        staleTime: 1000 * 60 * 5
    })

    const orgs = useMemo(() => {
        const list = Array.isArray(data) ? data : data?.results || []

        if (!activeOrg?.id || list.some((org) => org.id === activeOrg.id)) {
            return list
        }

        return [activeOrg, ...list]
    }, [activeOrg, data])

    if (!canSwitch) {
        return null
    }

    const selectedOrgId = activeOrg?.id || ''
    const disabled = isLoading || isSwitchingActiveOrg

    const handleOrgChange = async (event) => {
        const nextOrgId = event.target.value

        if (!nextOrgId || nextOrgId === selectedOrgId) {
            return
        }

        try {
            await switchActiveOrg(nextOrgId)
            toast.success('Active organisation changed.')
        } catch {
            toast.error('Unable to switch active organisation.')
        }
    }

    return (
        // In the xs top bar this shares a 343px row with three icon buttons:
        // let it shrink (the Select ellipsises the name) instead of pushing
        // the row wider than the phone. A long org name no longer widens it
        // without bound on desktop either.
        <FormControl
            size="small"
            sx={{
                flex: { xs: '0 1 160px', sm: '0 0 auto' },
                minWidth: { xs: 96, sm: 220 },
                maxWidth: { xs: 160, sm: 320 }
            }}
        >
            <InputLabel id={labelId}>Active org</InputLabel>
            <Select
                labelId={labelId}
                id={selectId}
                value={selectedOrgId}
                label="Active org"
                onChange={handleOrgChange}
                disabled={disabled}
            >
                <MenuItem value="" disabled>
                    Select organisation
                </MenuItem>
                {orgs.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                        {withSecondaryIdentifier(org.name, org.short_name)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}
